import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  DollarSign,
  Edit,
  FileText,
  Flag,
  FolderKanban,
  MessageSquare,
  Paperclip,
  Plus,
  Share2,
  Sparkles,
  Trash2,
  User,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const TASK_STATUSES = ["Todo", "In Progress", "Review", "Blocked", "Done"];
const DAY_MS = 24 * 60 * 60 * 1000;

const getStatusClass = (status) =>
  ({
    Planning: "status-planning",
    "In Progress": "status-in-progress",
    Blocked: "status-blocked",
    Completed: "status-completed",
  }[status] || "");

const getPriorityClass = (priority) =>
  ({
    Low: "priority-low",
    Medium: "priority-medium",
    High: "priority-high",
  }[priority] || "");

const getTaskTone = (status) =>
  ({
    Todo: "border-zinc-300 text-zinc-700 bg-zinc-50",
    "In Progress": "border-blue-200 text-blue-700 bg-blue-50",
    Review: "border-amber-200 text-amber-700 bg-amber-50",
    Blocked: "border-red-200 text-red-700 bg-red-50",
    Done: "border-emerald-200 text-emerald-700 bg-emerald-50",
  }[status] || "border-zinc-300 text-zinc-700 bg-zinc-50");

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDateLabel = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });
};

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentLabel = (attachment) => {
  const originalName = attachment?.original_name || "";
  const dotIndex = originalName.lastIndexOf(".");
  const extension = dotIndex > 0 ? originalName.slice(dotIndex) : "";
  const baseName = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
  const shortBase = baseName.slice(0, 10);
  return `${shortBase}${extension}`;
};

const getAttachmentPreviewType = (attachment) => {
  const contentType = attachment?.content_type || "";
  const fileName = (attachment?.original_name || "").toLowerCase();

  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf" || fileName.endsWith(".pdf")) return "pdf";
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";
  if (contentType.startsWith("text/") || /\.(txt|md|csv|json|log)$/i.test(fileName)) return "text";
  return "unsupported";
};

function TaskFormDialog({ open, onClose, onSubmit, initialData, loading, teamMembers, availableTasks }) {
  const [checklistDraft, setChecklistDraft] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    status: "Todo",
    assignee: "",
    due_date: "",
    notes: "",
    dependency_ids: [],
    estimated_hours: "",
    actual_hours: "",
    tags: "",
    recurrence_rule: "",
    checklist: [],
  });

  useEffect(() => {
    setFormData({
      title: initialData?.title || "",
      status: initialData?.status || "Todo",
      assignee: initialData?.assignee || "",
      due_date: initialData?.due_date || "",
      notes: initialData?.notes || "",
      dependency_ids: initialData?.dependency_ids || [],
      estimated_hours: initialData?.estimated_hours?.toString() || "",
      actual_hours: initialData?.actual_hours?.toString() || "",
      tags: (initialData?.tags || []).join(", "),
      recurrence_rule: initialData?.recurrence_rule || "",
      checklist: initialData?.checklist || [],
    });
    setChecklistDraft("");
  }, [initialData, open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.title.trim()) return;
    await onSubmit({
      title: formData.title.trim(),
      status: formData.status,
      assignee: formData.assignee,
      due_date: formData.due_date || null,
      notes: formData.notes.trim(),
      dependency_ids: formData.dependency_ids,
      estimated_hours: parseFloat(formData.estimated_hours) || 0,
      actual_hours: parseFloat(formData.actual_hours) || 0,
      tags: formData.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
      recurrence_rule: formData.recurrence_rule.trim(),
      checklist: formData.checklist,
    });
  };

  const addChecklistItem = () => {
    if (!checklistDraft.trim()) return;
    setFormData((current) => ({
      ...current,
      checklist: [...current.checklist, { id: crypto.randomUUID(), title: checklistDraft.trim(), owner: "", is_completed: false }],
    }));
    setChecklistDraft("");
  };

  const toggleDependency = (taskId) => {
    setFormData((current) => ({
      ...current,
      dependency_ids: current.dependency_ids.includes(taskId) ? current.dependency_ids.filter((item) => item !== taskId) : [...current.dependency_ids, taskId],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{initialData ? "Edit Task" : "Add Task"}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4 mt-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Task Title *</Label>
            <Input className="rounded-none border-zinc-300 focus:border-zinc-900" onChange={(event) => setFormData({ ...formData, title: event.target.value })} required value={formData.title} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Status</Label>
              <Select onValueChange={(value) => setFormData({ ...formData, status: value })} value={formData.status}>
                <SelectTrigger className="rounded-none border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Assignee</Label>
              <Select onValueChange={(value) => setFormData({ ...formData, assignee: value === "__unassigned__" ? "" : value })} value={formData.assignee || "__unassigned__"}>
                <SelectTrigger className="rounded-none border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {teamMembers.filter((member) => member.status !== "Inactive").map((member) => <SelectItem key={member.id} value={member.name}>{member.name}{member.role ? ` · ${member.role}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Due Date</Label>
              <Input className="rounded-none border-zinc-300 focus:border-zinc-900" onChange={(event) => setFormData({ ...formData, due_date: event.target.value })} type="date" value={formData.due_date} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Estimated Hours</Label>
              <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, estimated_hours: event.target.value })} type="number" min="0" step="0.5" value={formData.estimated_hours} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Actual Hours</Label>
              <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, actual_hours: event.target.value })} type="number" min="0" step="0.5" value={formData.actual_hours} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Tags</Label>
            <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, tags: event.target.value })} placeholder="design, launch, finance" value={formData.tags} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Recurring Task Rule</Label>
            <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, recurrence_rule: event.target.value })} placeholder="Every Monday / Monthly finance close" value={formData.recurrence_rule} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Dependencies</Label>
            <div className="max-h-32 overflow-y-auto border border-zinc-200 bg-zinc-50 p-3 space-y-2">
              {availableTasks.length === 0 ? <p className="text-sm text-zinc-400">No other tasks available.</p> : availableTasks.map((task) => (
                <label className="flex items-center gap-2 text-sm text-zinc-700" key={task.id}>
                  <input checked={formData.dependency_ids.includes(task.id)} onChange={() => toggleDependency(task.id)} type="checkbox" />
                  <span>{task.title}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Checklist</Label>
            <div className="space-y-2 border border-zinc-200 bg-zinc-50 p-3">
              {(formData.checklist || []).map((item) => (
                <div className="grid grid-cols-[20px_minmax(0,1fr)_minmax(140px,180px)_20px] gap-3 items-center" key={item.id}>
                  <input checked={item.is_completed} onChange={() => setFormData((current) => ({ ...current, checklist: current.checklist.map((entry) => entry.id === item.id ? { ...entry, is_completed: !entry.is_completed } : entry) }))} type="checkbox" />
                  <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData((current) => ({ ...current, checklist: current.checklist.map((entry) => entry.id === item.id ? { ...entry, title: event.target.value } : entry) }))} value={item.title} />
                  <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData((current) => ({ ...current, checklist: current.checklist.map((entry) => entry.id === item.id ? { ...entry, owner: event.target.value } : entry) }))} placeholder="Owner" value={item.owner || ""} />
                  <button className="text-zinc-400 hover:text-red-600" onClick={() => setFormData((current) => ({ ...current, checklist: current.checklist.filter((entry) => entry.id !== item.id) }))} type="button"><Trash2 size={14} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setChecklistDraft(event.target.value)} placeholder="Add checklist item" value={checklistDraft} />
                <Button className="rounded-none" onClick={addChecklistItem} type="button">Add</Button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Notes</Label>
            <Textarea className="rounded-none border-zinc-300 focus:border-zinc-900 min-h-28" onChange={(event) => setFormData({ ...formData, notes: event.target.value })} value={formData.notes} />
          </div>
          <div className="flex flex-wrap justify-end gap-3 pt-3">
            <Button className="rounded-none border-zinc-300" onClick={() => onClose(false)} type="button" variant="outline">Cancel</Button>
            <Button className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-6 text-xs font-bold uppercase tracking-wider" disabled={loading} type="submit">{loading ? "Saving..." : initialData ? "Update Task" : "Create Task"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CommentFormDialog({ open, onClose, onSubmit, initialData, loading }) {
  const [formData, setFormData] = useState({ author: "", body: "" });
  useEffect(() => {
    setFormData({ author: initialData?.author || "", body: initialData?.body || "" });
  }, [initialData, open]);
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.body.trim()) return;
    await onSubmit({ author: formData.author.trim(), body: formData.body.trim() });
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{initialData ? "Edit Comment" : "Add Comment"}</DialogTitle></DialogHeader>
        <form className="space-y-4 mt-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Author</Label>
            <Input className="rounded-none border-zinc-300" onChange={(event) => setFormData({ ...formData, author: event.target.value })} value={formData.author} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Comment *</Label>
            <Textarea className="rounded-none border-zinc-300 min-h-28" onChange={(event) => setFormData({ ...formData, body: event.target.value })} value={formData.body} />
          </div>
          <div className="flex flex-wrap justify-end gap-3 pt-3">
            <Button className="rounded-none border-zinc-300" onClick={() => onClose(false)} type="button" variant="outline">Cancel</Button>
            <Button className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-6 text-xs font-bold uppercase tracking-wider" disabled={loading} type="submit">{loading ? "Saving..." : initialData ? "Update Comment" : "Add Comment"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GanttChart({ tasks, projectCreatedAt, onOpenTask }) {
  const [zoom, setZoom] = useState("month");
  const [filters, setFilters] = useState({ task: "", owner: "", start: "", due: "", dependencies: "" });
  const rawTimeline = useMemo(() => buildGanttTimeline(tasks, projectCreatedAt, zoom), [projectCreatedAt, tasks, zoom]);
  const dependencyTitleMap = useMemo(() => new Map(tasks.map((task) => [task.id, task.title || ""])), [tasks]);

  const timeline = useMemo(() => {
    if (!rawTimeline) return null;
    const normalized = Object.fromEntries(Object.entries(filters).map(([key, value]) => [key, String(value || "").trim().toLowerCase()]));
    const bars = rawTimeline.bars.filter((task) => {
      const dependencyTitles = (task.dependency_ids || []).map((id) => dependencyTitleMap.get(id) || id).join(" ").toLowerCase();
      const taskTitle = String(task.title || "").toLowerCase();
      const owner = String(task.assignee || "unassigned").toLowerCase();
      const start = formatDateLabel(task.start.toISOString()).toLowerCase();
      const due = formatDateLabel(task.end.toISOString()).toLowerCase();
      const dependencies = `${(task.dependency_ids || []).length} ${dependencyTitles}`.toLowerCase();
      return (!normalized.task || taskTitle.includes(normalized.task))
        && (!normalized.owner || owner.includes(normalized.owner))
        && (!normalized.start || start.includes(normalized.start))
        && (!normalized.due || due.includes(normalized.due))
        && (!normalized.dependencies || dependencies.includes(normalized.dependencies));
    });
    return { ...rawTimeline, bars };
  }, [dependencyTitleMap, filters, rawTimeline]);

  if (!timeline || timeline.bars.length === 0) return <p className="text-sm text-zinc-400">Add due dates to tasks or relax the filters to populate the Gantt chart.</p>;

  const tableWidth = 700;
  const tableColumns = '240px 120px 120px 120px 100px';

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {[['week', 'Week'], ['month', 'Month'], ['quarter', 'Quarter']].map(([value, label]) => (
            <Button className="rounded-none" key={value} onClick={() => setZoom(value)} size="sm" type="button" variant={zoom === value ? 'default' : 'outline'}>
              {label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
          <span>{timeline.bars.length} scheduled items</span>
          <span>{timeline.milestoneCount} milestones</span>
          <span>{timeline.dependencyCount} dependencies</span>
        </div>
      </div>

      <div className="gantt-legend">
        <span><i className="gantt-chip gantt-chip-todo" />Todo</span>
        <span><i className="gantt-chip gantt-chip-in-progress" />In Progress</span>
        <span><i className="gantt-chip gantt-chip-review" />Review</span>
        <span><i className="gantt-chip gantt-chip-blocked" />Blocked</span>
        <span><i className="gantt-chip gantt-chip-done" />Done</span>
        <span><i className="gantt-chip gantt-chip-milestone" />Milestone</span>
      </div>

      <div className="gantt-shell">
        <div className="gantt-header" style={{ gridTemplateColumns: `${tableWidth}px repeat(${timeline.columns.length}, minmax(${timeline.columnMinWidth}px, 1fr))` }}>
          <div className="gantt-table-header-wrap">
            <div className="gantt-table-header" style={{ gridTemplateColumns: tableColumns }}>
              <div className="gantt-table-header-cell">Task</div>
              <div className="gantt-table-header-cell">Owner</div>
              <div className="gantt-table-header-cell">Start</div>
              <div className="gantt-table-header-cell">Due</div>
              <div className="gantt-table-header-cell">Dependencies</div>
            </div>
            <div className="gantt-table-filters" style={{ gridTemplateColumns: tableColumns }}>
              <Input className="gantt-filter-input" onChange={(event) => setFilters((current) => ({ ...current, task: event.target.value }))} placeholder="Filter tasks" value={filters.task} />
              <Input className="gantt-filter-input" onChange={(event) => setFilters((current) => ({ ...current, owner: event.target.value }))} placeholder="Owner" value={filters.owner} />
              <Input className="gantt-filter-input" onChange={(event) => setFilters((current) => ({ ...current, start: event.target.value }))} placeholder="Start" value={filters.start} />
              <Input className="gantt-filter-input" onChange={(event) => setFilters((current) => ({ ...current, due: event.target.value }))} placeholder="Due" value={filters.due} />
              <Input className="gantt-filter-input" onChange={(event) => setFilters((current) => ({ ...current, dependencies: event.target.value }))} placeholder="Deps" value={filters.dependencies} />
            </div>
          </div>
          {timeline.columns.map((column, index) => <div className="gantt-header-cell" key={`${column.label}-${index}`}>{column.label}</div>)}
        </div>
        <div className="gantt-body">
          {timeline.bars.map((task) => {
            const dependencyTitles = (task.dependency_ids || []).map((id) => dependencyTitleMap.get(id) || id).filter(Boolean);
            return (
              <div className={`gantt-row ${task.isMilestone ? 'gantt-row-milestone' : ''}`} key={task.id} style={{ gridTemplateColumns: `${tableWidth}px minmax(${timeline.trackMinWidth}px, 1fr)` }}>
                <div className="gantt-table-row" style={{ gridTemplateColumns: tableColumns }}>
                  <button className="gantt-table-task-cell" onClick={() => onOpenTask(task.id)} type="button">
                    <span>{task.title}</span>
                    {task.phaseTag ? <small>{task.phaseTag.replace(/-/g, ' ')}</small> : null}
                    {task.lateByDays ? <strong className="gantt-slip-badge">+{task.lateByDays}d slip</strong> : null}
                  </button>
                  <div className="gantt-table-cell">{task.assignee || 'Unassigned'}</div>
                  <div className="gantt-table-cell">{formatDateLabel(task.start.toISOString())}</div>
                  <div className="gantt-table-cell">{formatDateLabel(task.end.toISOString())}</div>
                  <div className="gantt-table-cell">{dependencyTitles.length ? dependencyTitles.join(', ') : 'None'}</div>
                </div>
                <div className="gantt-track" style={{ backgroundSize: `${100 / Math.max(timeline.columns.length, 1)}% 100%` }}>
                  {timeline.todayOffset !== null ? <div className="gantt-today-marker" style={{ left: `${timeline.todayOffset}%` }} /> : null}
                  <button className={`gantt-bar gantt-${String(task.status || 'todo').toLowerCase().replace(/\s+/g, '-')} ${task.isMilestone ? 'gantt-bar-milestone' : ''}`} onClick={() => onOpenTask(task.id)} style={{ left: `${task.left}%`, width: `${task.width}%` }} type="button">
                    <span>{task.isMilestone ? 'Milestone' : task.title}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TaskCard({ task, projectId, teamMembers, highlighted, readOnly, allTasks, onEditTask, onDeleteTask, onCreateSubtask, onUpdateSubtask, onDeleteSubtask, onCreateComment, onUpdateComment, onDeleteComment, onUploadAttachment, onDeleteAttachment, onUpdateTask }) {
  const [detailsOpen, setDetailsOpen] = useState(Boolean(highlighted));
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [savingComment, setSavingComment] = useState(false);
  const [uploading, setUploading] = useState(false);
  const subtasks = task.subtasks || [];
  const comments = task.comments || [];
  const attachments = task.attachments || [];
  const checklist = task.checklist || [];

  const handleSubtaskSubmit = async () => {
    if (!subtaskTitle.trim()) return;
    await onCreateSubtask(projectId, task.id, { title: subtaskTitle.trim(), is_completed: false });
    setSubtaskTitle("");
  };

  const handleCommentSubmit = async (data) => {
    setSavingComment(true);
    try {
      if (commentDraft?.id) await onUpdateComment(projectId, task.id, commentDraft.id, data);
      else await onCreateComment(projectId, task.id, data);
      setCommentDraft(null);
      setCommentDialogOpen(false);
    } finally {
      setSavingComment(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUploadAttachment(projectId, task.id, file);
    } finally {
      event.target.value = "";
      setUploading(false);
    }
  };

  const toggleChecklist = async (itemId) => {
    const nextChecklist = checklist.map((item) => item.id === itemId ? { ...item, is_completed: !item.is_completed } : item);
    await onUpdateTask(projectId, task.id, { checklist: nextChecklist });
  };

  const dependencyTitles = (task.dependency_ids || []).map((id) => allTasks.find((item) => item.id === id)?.title).filter(Boolean);

  useEffect(() => {
    if (highlighted) setDetailsOpen(true);
  }, [highlighted]);

  return (
    <div className={`border bg-white p-5 space-y-5 transition-all ${highlighted ? "border-blue-400 ring-2 ring-blue-100 shadow-sm" : "border-zinc-200"}`} id={`task-${task.id}`}>
      <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
        <button className="min-w-0 flex-1 text-left" onClick={() => setDetailsOpen((current) => !current)} type="button">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            {detailsOpen ? <ChevronDown className="text-zinc-400" size={16} /> : <ChevronRight className="text-zinc-400" size={16} />}
            <h4 className="text-lg font-semibold text-zinc-900">{task.title}</h4>
            <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest border ${getTaskTone(task.status)}`}>{task.status}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500">
            <span>Owner: {task.assignee || "Unassigned"}</span>
            <span>Due: {task.due_date ? formatDateLabel(task.due_date) : "Not set"}</span>
            <span>Hours: {task.actual_hours || 0}/{task.estimated_hours || 0}</span>
            <span>Checklist: {checklist.filter((item) => item.is_completed).length}/{checklist.length}</span>
          </div>
          {task.tags?.length ? <div className="mt-3 flex flex-wrap gap-2">{task.tags.map((tag) => <span className="px-2 py-1 border border-zinc-200 text-xs uppercase tracking-[0.15em] text-zinc-500" key={tag}>{tag}</span>)}</div> : null}
          {task.notes ? <p className="mt-3 text-sm leading-6 text-zinc-700">{task.notes}</p> : null}
          {task.recurrence_rule ? <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Recurring: {task.recurrence_rule}</p> : null}
          {dependencyTitles.length ? <p className="mt-2 text-sm text-zinc-500">Depends on: {dependencyTitles.join(", ")}</p> : null}
        </button>
        {!readOnly ? (
          <div className="flex items-center gap-2 shrink-0">
            <Button className="rounded-none border-zinc-300" onClick={() => onEditTask(task)} size="sm" type="button" variant="outline"><Edit size={14} /></Button>
            <Button className="rounded-none border-red-300 text-red-600 hover:bg-red-50" onClick={() => onDeleteTask(projectId, task.id)} size="sm" type="button" variant="outline"><Trash2 size={14} /></Button>
          </div>
        ) : null}
      </div>

      {detailsOpen ? <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <div className="border border-zinc-200 bg-zinc-50/40 p-4">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="text-zinc-500" size={16} /><h5 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Checklist</h5></div>
          <div className="space-y-3">
            {checklist.length === 0 ? <p className="text-sm text-zinc-400">No checklist items yet.</p> : checklist.map((item) => (
              <label className="flex items-start gap-3" key={item.id}>
                <input checked={item.is_completed} className="mt-1 h-4 w-4 accent-black" disabled={readOnly} onChange={() => toggleChecklist(item.id)} type="checkbox" />
                <div className="flex-1">
                  <p className={`text-sm ${item.is_completed ? "line-through text-zinc-400" : "text-zinc-700"}`}>{item.title}</p>
                  {item.owner ? <p className="text-xs text-zinc-400 mt-1">Owner: {item.owner}</p> : null}
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="border border-zinc-200 bg-zinc-50/40 p-4">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="text-zinc-500" size={16} /><h5 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Subtasks</h5></div>
          <div className="space-y-3">
            {subtasks.map((subtask) => (
              <div className="flex items-start gap-3" key={subtask.id}>
                <input checked={subtask.is_completed} className="mt-1 h-4 w-4 accent-black" disabled={readOnly} onChange={() => onUpdateSubtask(projectId, task.id, subtask.id, { is_completed: !subtask.is_completed })} type="checkbox" />
                <p className={`flex-1 text-sm ${subtask.is_completed ? "line-through text-zinc-400" : "text-zinc-700"}`}>{subtask.title}</p>
                {!readOnly ? <button className="text-zinc-400 hover:text-red-600" onClick={() => onDeleteSubtask(projectId, task.id, subtask.id)} type="button"><Trash2 size={14} /></button> : null}
              </div>
            ))}
            {subtasks.length === 0 ? <p className="text-sm text-zinc-400">No subtasks yet.</p> : null}
          </div>
          {!readOnly ? <div className="mt-4 flex gap-2"><Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setSubtaskTitle(event.target.value)} placeholder="Add subtask" value={subtaskTitle} /><Button className="rounded-none shrink-0" onClick={handleSubtaskSubmit} type="button">Add</Button></div> : null}
        </div>
        <div className="border border-zinc-200 bg-zinc-50/40 p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2"><MessageSquare className="text-zinc-500" size={16} /><h5 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Comments</h5></div>
            {!readOnly ? <Button className="rounded-none border-zinc-300" onClick={() => { setCommentDraft(null); setCommentDialogOpen(true); }} size="sm" type="button" variant="outline"><Plus size={14} /></Button> : null}
          </div>
          <div className="space-y-3">
            {comments.map((comment) => (
              <div className="border border-zinc-200 bg-white p-3" key={comment.id}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div><p className="text-sm font-semibold text-zinc-900">{comment.author || "Anonymous"}</p><p className="text-xs text-zinc-400">{formatDateLabel(comment.created_at)}</p></div>
                  {!readOnly ? <div className="flex items-center gap-2"><button className="text-zinc-400 hover:text-zinc-700" onClick={() => { setCommentDraft(comment); setCommentDialogOpen(true); }} type="button"><Edit size={14} /></button><button className="text-zinc-400 hover:text-red-600" onClick={() => onDeleteComment(projectId, task.id, comment.id)} type="button"><Trash2 size={14} /></button></div> : null}
                </div>
                <p className="text-sm leading-6 text-zinc-700 whitespace-pre-wrap">{comment.body}</p>
              </div>
            ))}
            {comments.length === 0 ? <p className="text-sm text-zinc-400">No comments yet.</p> : null}
          </div>
        </div>
        <div className="border border-zinc-200 bg-zinc-50/40 p-4">
          <div className="flex items-center gap-2 mb-3"><Paperclip className="text-zinc-500" size={16} /><h5 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Attachments</h5></div>
          {!readOnly ? <input className="block w-full text-sm text-zinc-600 mb-4" onChange={handleUpload} type="file" /> : null}
          {uploading ? <p className="text-sm text-zinc-400 mb-3">Uploading...</p> : null}
          <div className="space-y-3">
            {attachments.map((attachment) => (
              <div className="border border-zinc-200 bg-white p-3" key={attachment.id}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-zinc-900">{getAttachmentLabel(attachment)}</p>
                  <p className="mt-1 text-xs text-zinc-400">{formatFileSize(attachment.size_bytes)} · {formatDateLabel(attachment.uploaded_at)}</p>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button className="inline-flex items-center justify-center px-2 py-1 border border-zinc-300 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50" onClick={() => setPreviewAttachment(attachment)} type="button">Preview</button>
                  <a className="inline-flex items-center justify-center px-2 py-1 border border-zinc-300 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50" href={attachment.download_url} rel="noreferrer" target="_blank">Open</a>
                  {!readOnly ? <button className="text-zinc-400 hover:text-red-600" onClick={() => onDeleteAttachment(projectId, task.id, attachment.id)} type="button"><Trash2 size={14} /></button> : null}
                </div>
              </div>
            ))}
            {attachments.length === 0 ? <p className="text-sm text-zinc-400">No attachments yet.</p> : null}
          </div>
        </div>
      </div> : null}
      <CommentFormDialog initialData={commentDraft} loading={savingComment} onClose={(open) => { if (!open) { setCommentDialogOpen(false); setCommentDraft(null); } }} onSubmit={handleCommentSubmit} open={commentDialogOpen} />
      <AttachmentPreviewDialog attachment={previewAttachment} onClose={(open) => { if (!open) setPreviewAttachment(null); }} open={!!previewAttachment} />
    </div>
  );
}

function AttachmentPreviewDialog({ attachment, open, onClose }) {
  const previewType = getAttachmentPreviewType(attachment);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">
            {attachment?.original_name || "Attachment Preview"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-500">
            {attachment ? `${formatFileSize(attachment.size_bytes)} · ${formatDateLabel(attachment.uploaded_at)}` : ""}
          </p>
          {previewType === "image" ? (
            <div className="border border-zinc-200 bg-zinc-50 p-3">
              <img alt={attachment.original_name} className="max-h-[70vh] w-full object-contain" src={attachment.preview_url || attachment.download_url} />
            </div>
          ) : null}
          {previewType === "pdf" ? <iframe className="h-[70vh] w-full border border-zinc-200 bg-white" src={attachment.preview_url || attachment.download_url} title={attachment.original_name} /> : null}
          {previewType === "video" ? <video className="max-h-[70vh] w-full border border-zinc-200 bg-black" controls src={attachment.preview_url || attachment.download_url} /> : null}
          {previewType === "audio" ? <div className="border border-zinc-200 bg-zinc-50 p-6"><audio className="w-full" controls src={attachment.preview_url || attachment.download_url} /></div> : null}
          {previewType === "text" ? <iframe className="h-[70vh] w-full border border-zinc-200 bg-white" src={attachment.preview_url || attachment.download_url} title={attachment.original_name} /> : null}
          {previewType === "unsupported" ? (
            <div className="border border-dashed border-zinc-300 bg-zinc-50 p-6 text-sm text-zinc-600">
              Preview is not available for this file type. Open the attachment in a new tab instead.
            </div>
          ) : null}
          <div className="flex justify-end">
            <a className="inline-flex items-center px-3 py-2 border border-zinc-300 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50" href={attachment?.download_url} rel="noreferrer" target="_blank">
              Open In New Tab
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ProjectDetail = ({ project, company, business, activity = [], currentUser, onEdit, onDelete, onBack, onCreateTask, onUpdateTask, onDeleteTask, onCreateSubtask, onUpdateSubtask, onDeleteSubtask, onCreateComment, onUpdateComment, onDeleteComment, onUploadAttachment, onDeleteAttachment, teamMembers = [], selectedTaskId, onClearTaskSelection, onCreateShareLink, onGenerateProjectSummary, exportTasksUrl, exportProjectsUrl }) => {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [submittingTask, setSubmittingTask] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [projectAiSummary, setProjectAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [taskSearch, setTaskSearch] = useState("");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const readOnly = currentUser?.role === "viewer";

  const tasks = useMemo(() => project.tasks || [], [project.tasks]);
  const projectAttachments = useMemo(
    () => tasks.flatMap((task) => (task.attachments || []).map((attachment) => ({ ...attachment, task_id: task.id, task_title: task.title }))),
    [tasks],
  );

  useEffect(() => {
    if (!selectedTaskId) return;
    setActiveTab("tasks");
    const selectedTask = tasks.find((task) => task.id === selectedTaskId);
    if (selectedTask) {
      const groupKey = getTaskGroupKey(selectedTask);
      setCollapsedGroups((current) => ({ ...current, [groupKey]: false }));
    }
    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(`task-${selectedTaskId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      const clearId = window.setTimeout(() => onClearTaskSelection?.(), 2500);
      return () => window.clearTimeout(clearId);
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [onClearTaskSelection, selectedTaskId, tasks]);

  const taskSummary = useMemo(() => {
    const completed = tasks.filter((task) => task.status === "Done").length;
    const blocked = tasks.filter((task) => task.status === "Blocked").length;
    const overdue = tasks.filter((task) => task.due_date && task.status !== "Done" && new Date(task.due_date) < new Date()).length;
    const estimatedHours = tasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);
    const actualHours = tasks.reduce((sum, task) => sum + (task.actual_hours || 0), 0);
    return { completed, blocked, overdue, total: tasks.length, estimatedHours, actualHours };
  }, [tasks]);

  const taskGroups = useMemo(() => {
    const query = taskSearch.trim().toLowerCase();
    const filtered = tasks.filter((task) => {
      if (taskStatusFilter !== "all" && String(task.status || "Todo") !== taskStatusFilter) return false;
      if (!query) return true;
      const haystack = [
        task.title,
        task.assignee,
        task.notes,
        ...(task.tags || []),
        ...(task.dependency_ids || []).map(String),
      ].join(" ").toLowerCase();
      return haystack.includes(query);
    });

    const groups = new Map();
    filtered.forEach((task) => {
      const groupKey = getTaskGroupKey(task);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          label: formatTaskGroupLabel(groupKey),
          tasks: [],
          completed: 0,
          blocked: 0,
          totalHours: 0,
        });
      }
      const group = groups.get(groupKey);
      group.tasks.push(task);
      if (task.status === "Done") group.completed += 1;
      if (task.status === "Blocked") group.blocked += 1;
      group.totalHours += task.estimated_hours || 0;
    });

    return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [taskSearch, taskStatusFilter, tasks]);

  const visibleTaskCount = useMemo(() => taskGroups.reduce((sum, group) => sum + group.tasks.length, 0), [taskGroups]);

  const handleTaskSubmit = async (data) => {
    setSubmittingTask(true);
    try {
      if (editingTask) await onUpdateTask(project.id, editingTask.id, data);
      else await onCreateTask(project.id, data);
      setShowTaskForm(false);
      setEditingTask(null);
      setActiveTab("tasks");
    } finally {
      setSubmittingTask(false);
    }
  };

  const handleCreateShare = async () => {
    const result = await onCreateShareLink(project.id);
    if (result) setShareLinks((current) => [result, ...current]);
  };

  const handleGenerateSummary = async () => {
    setSummaryLoading(true);
    try {
      const result = await onGenerateProjectSummary(project.id);
      setProjectAiSummary(result);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleOpenStandaloneGantt = () => {
    const datedTasks = tasks.filter((task) => task.due_date);
    const timeline = buildGanttTimeline(tasks, project.created_at, "month");
    const rows = datedTasks.map((task) => `
      <tr>
        <td>${escapeHtml(task.title)}</td>
        <td>${escapeHtml(task.assignee || "Unassigned")}</td>
        <td>${escapeHtml(task.status || "Todo")}</td>
        <td>${escapeHtml(formatDateLabel(task.created_at || project.created_at))}</td>
        <td>${escapeHtml(formatDateLabel(task.due_date))}</td>
        <td>${escapeHtml((task.dependency_ids || []).length ? String(task.dependency_ids.length) : "0")}</td>
      </tr>
    `).join("");
    const ganttRows = timeline ? timeline.bars.map((task) => `
      <div class="gantt-row ${task.isMilestone ? "gantt-row-milestone" : ""}">
        <div class="gantt-label">
          <div class="gantt-title">${escapeHtml(task.title)}</div>
          <div class="gantt-owner">${escapeHtml(task.assignee || "Unassigned")}</div>
          <div class="gantt-meta">${escapeHtml(formatDateLabel(task.start.toISOString()))} to ${escapeHtml(formatDateLabel(task.end.toISOString()))}${task.dependency_ids?.length ? ` • ${escapeHtml(String(task.dependency_ids.length))} dep` : ""}${task.lateByDays ? ` • ${escapeHtml(String(task.lateByDays))}d slip` : ""}</div>
        </div>
        <div class="gantt-track" style="background-size:${100 / Math.max(timeline.columns.length, 1)}% 100%;">
          ${timeline.todayOffset !== null ? `<div class="gantt-today-marker" style="left:${timeline.todayOffset}%;"></div>` : ""}
          <div class="gantt-bar gantt-${escapeHtml(String(task.status || "todo").toLowerCase().replace(/\s+/g, "-"))} ${task.isMilestone ? "gantt-bar-milestone" : ""}" style="left:${task.left}%;width:${task.width}%;">
            ${escapeHtml(task.isMilestone ? "Milestone" : task.title)}
          </div>
        </div>
      </div>
    `).join("") : "";
    const ganttHeader = timeline ? `<div class="gantt-header-spacer">Task</div>${timeline.columns.map((column) => `<div class="gantt-header-cell">${escapeHtml(column.label)}</div>`).join("")}` : "";
    const popupHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(project.name)} - Gantt Planner</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; background: #f4f4f5; color: #18181b; }
    .shell { padding: 28px; }
    .header { display: flex; justify-content: space-between; gap: 16px; align-items: flex-end; margin-bottom: 24px; }
    .eyebrow { font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase; color: #71717a; font-weight: 700; }
    h1 { margin: 8px 0 0; font-size: 40px; line-height: 1; }
    p { color: #52525b; margin: 8px 0 0; }
    .card { background: white; border: 1px solid #e4e4e7; padding: 20px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #e4e4e7; padding: 10px 12px; text-align: left; font-size: 14px; }
    th { background: #fafafa; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: #71717a; }
    .meta { text-align: right; font-size: 14px; color: #3f3f46; }
    .empty { color: #71717a; font-size: 14px; }
    .gantt-shell { overflow-x: auto; border: 1px solid #e4e4e7; background: #fff; }
    .gantt-header { display: grid; grid-template-columns: 260px repeat(${timeline ? timeline.columns.length : 1}, minmax(${timeline ? timeline.columnMinWidth : 72}px, 1fr)); border-bottom: 1px solid #e4e4e7; min-width: max-content; }
    .gantt-header-spacer { padding: 10px 16px; background: #fafafa; border-right: 1px solid #e4e4e7; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #71717a; }
    .gantt-header-cell { padding: 10px 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #71717a; border-left: 1px solid #f4f4f5; text-align: center; background: #fafafa; }
    .gantt-row { display: grid; grid-template-columns: 260px minmax(${timeline ? timeline.trackMinWidth : 640}px, 1fr); min-width: max-content; border-bottom: 1px solid #f4f4f5; }
    .gantt-label { padding: 14px 16px; border-right: 1px solid #e4e4e7; background: #fff; }
    .gantt-title { font-size: 14px; font-weight: 700; color: #18181b; }
    .gantt-owner { margin-top: 4px; font-size: 12px; color: #71717a; }
    .gantt-meta { margin-top: 6px; font-size: 11px; color: #71717a; text-transform: uppercase; letter-spacing: 0.08em; }
    .gantt-track { position: relative; height: 68px; background: repeating-linear-gradient(to right, #f4f4f5 0, #f4f4f5 calc(100% - 1px), #e4e4e7 calc(100% - 1px), #e4e4e7 100%); }
    .gantt-bar { position: absolute; top: 18px; height: 32px; border-radius: 0; padding: 0 10px; display: flex; align-items: center; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: white; overflow: hidden; white-space: nowrap; z-index: 2; }
    .gantt-bar-milestone { min-width: 18px; justify-content: center; clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%); padding: 0; }
    .gantt-today-marker { position: absolute; top: 0; bottom: 0; width: 2px; background: #dc2626; z-index: 1; }
    .gantt-todo { background: #52525b; }
    .gantt-in-progress { background: #1d4ed8; }
    .gantt-review { background: #d97706; }
    .gantt-blocked { background: #dc2626; }
    .gantt-done { background: #059669; }
  </style>
</head>
<body>
  <div class="shell">
    <div class="header">
      <div>
        <div class="eyebrow">Standalone Gantt Planner</div>
        <h1>${escapeHtml(project.name)}</h1>
        <p>${escapeHtml(company.name)} / ${escapeHtml(business.name)}</p>
      </div>
      <div class="meta">
        <div>Tasks with dates: ${datedTasks.length}</div>
        <div>Generated: ${escapeHtml(new Date().toLocaleString())}</div>
      </div>
    </div>
    <div class="card">
      <div class="eyebrow" style="margin-bottom:12px;">Timeline</div>
      ${timeline ? `<div class="gantt-shell"><div class="gantt-header">${ganttHeader}</div><div>${ganttRows}</div></div>` : `<div class="empty">Add due dates to tasks to populate the planner.</div>`}
    </div>
    <div class="card">
      <div class="eyebrow" style="margin-bottom:12px;">Task Register</div>
      ${rows ? `<table><thead><tr><th>Task</th><th>Owner</th><th>Status</th><th>Start</th><th>Due</th><th>Dependencies</th></tr></thead><tbody>${rows}</tbody></table>` : `<div class="empty">No dated tasks yet.</div>`}
    </div>
  </div>
</body>
</html>`;

    const popup = window.open("about:blank", "_blank", "width=1480,height=900,scrollbars=yes,resizable=yes");
    if (!popup) return;

    try {
      popup.document.open();
      popup.document.write(popupHtml);
      popup.document.close();
      popup.focus();
    } catch (error) {
      const blob = new Blob([popupHtml], { type: "text/html" });
      const objectUrl = URL.createObjectURL(blob);
      popup.location.href = objectUrl;
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    }
  };

  const overviewPanel = (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <DetailCard icon={<Activity size={18} />} label="Status" value={project.status} />
        <DetailCard icon={<Flag size={18} />} label="Priority" value={project.priority} />
        <DetailCard icon={<User size={18} />} label="Lead" value={project.lead || "Not assigned"} />
        <DetailCard icon={<DollarSign size={18} />} isMono label="Budget" value={formatCurrency(project.budget)} />
        <DetailCard icon={<Clock3 size={18} />} label="Est. Hours" value={taskSummary.estimatedHours} />
        <DetailCard icon={<Clock3 size={18} />} label="Actual Hours" value={taskSummary.actualHours} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
        <TaskSummaryCard label="Tasks" value={taskSummary.total} />
        <TaskSummaryCard label="Done" value={taskSummary.completed} />
        <TaskSummaryCard label="Blocked" value={taskSummary.blocked} />
        <TaskSummaryCard label="Overdue" value={taskSummary.overdue} />
        <TaskSummaryCard label="Share Links" value={shareLinks.length} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] gap-8">
        <div className="space-y-8">
          {projectAiSummary ? (
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-4"><Sparkles className="text-zinc-400" size={18} /><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">AI Project Summary</h3></div>
              {projectAiSummary.configured === false ? <p className="text-sm text-zinc-500">Add `OPENAI_API_KEY` on the backend to enable AI summaries.</p> : (
                <div className="space-y-4">
                  <p className="text-sm text-zinc-700">{projectAiSummary.summary}</p>
                  <div><p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Status Update</p><p className="text-sm text-zinc-700">{projectAiSummary.status_update}</p></div>
                  <ListBlock title="Risks" items={projectAiSummary.risks} />
                  <ListBlock title="Next Steps" items={projectAiSummary.next_steps} />
                </div>
              )}
            </div>
          ) : null}

          <div className="card p-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3"><Calendar className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Planning Timeline</h3><p className="text-sm text-zinc-500">Open a dedicated planner window instead of working around a long page.</p></div></div>
              <Button className="rounded-none border-zinc-300" onClick={handleOpenStandaloneGantt} size="sm" type="button" variant="outline">Open Gantt Window</Button>
            </div>
            <div className="text-sm text-zinc-600">Use the dedicated Gantt view to plan without scrolling through the task register. Keep this overview page focused on status and decisions.</div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="card p-6">
            <div className="flex items-center gap-3"><Calendar className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Deadline</h3><p className="font-mono text-lg text-zinc-900">{project.deadline ? new Date(project.deadline).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "No deadline set"}</p></div></div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4"><Share2 className="text-zinc-400" size={18} /><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Client Share Links</h3></div>
            <div className="space-y-3">
              {shareLinks.length === 0 ? <p className="text-sm text-zinc-400">No share links created yet.</p> : shareLinks.map((item) => <div className="border border-zinc-200 p-3 bg-zinc-50/50" key={item.id}><a className="text-sm font-semibold text-zinc-900 break-all" href={item.share_url} rel="noreferrer" target="_blank">{item.share_url}</a><p className="text-xs text-zinc-400 mt-1">Expires {formatDateLabel(item.expires_at)}</p></div>)}
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4"><User className="text-zinc-400" size={18} /><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Assignable Team</h3></div>
            <div className="space-y-3">
              {teamMembers.length === 0 ? <p className="text-sm text-zinc-400">No team members added yet.</p> : teamMembers.map((member) => <div className="border border-zinc-200 p-3 bg-zinc-50/50" key={member.id}><p className="font-semibold text-zinc-900">{member.name}</p><p className="text-sm text-zinc-500">{member.role || "No role set"}</p><p className="text-xs text-zinc-400">{member.status}</p></div>)}
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4"><FileText className="text-zinc-400" size={18} /><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Project Context</h3></div>
            <div className="grid grid-cols-1 gap-4 text-sm">
              <div><span className="text-zinc-500">Company:</span><span className="ml-2 font-medium text-zinc-900">{company.name}</span></div>
              <div><span className="text-zinc-500">Business:</span><span className="ml-2 font-medium text-zinc-900">{business.name}</span></div>
              <div><span className="text-zinc-500">Created:</span><span className="ml-2 font-medium text-zinc-900">{new Date(project.created_at).toLocaleDateString()}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tasksPanel = (
    <div className="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)] gap-6 items-start">
      <div className="card p-4 xl:sticky xl:top-24 space-y-4">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Task Navigation</h3>
          <p className="mt-2 text-sm text-zinc-500">Jump by workstream instead of scrolling one long register.</p>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Search</Label>
            <Input className="mt-2 rounded-none border-zinc-300" onChange={(event) => setTaskSearch(event.target.value)} placeholder="Search title, owner, tags" value={taskSearch} />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Status</Label>
            <Select onValueChange={setTaskStatusFilter} value={taskStatusFilter}>
              <SelectTrigger className="mt-2 rounded-none border-zinc-300">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {TASK_STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="border border-zinc-200 bg-zinc-50 px-2 py-3"><div className="text-lg font-semibold text-zinc-900">{visibleTaskCount}</div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Visible</div></div>
          <div className="border border-zinc-200 bg-zinc-50 px-2 py-3"><div className="text-lg font-semibold text-zinc-900">{taskGroups.length}</div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Groups</div></div>
          <div className="border border-zinc-200 bg-zinc-50 px-2 py-3"><div className="text-lg font-semibold text-zinc-900">{taskSummary.overdue}</div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Overdue</div></div>
        </div>
        <div className="space-y-2 max-h-[55vh] overflow-auto pr-1">
          {taskGroups.map((group) => {
            const isCollapsed = collapsedGroups[group.key] ?? false;
            return (
              <button
                className="task-group-nav w-full text-left"
                key={group.key}
                onClick={() => {
                  setCollapsedGroups((current) => ({ ...current, [group.key]: false }));
                  window.setTimeout(() => {
                    document.getElementById(`task-group-${group.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 40);
                }}
                type="button"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-zinc-900">{group.label}</div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">{group.tasks.length} tasks</div>
                  </div>
                  <ChevronDown className={`text-zinc-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} size={16} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between gap-4 mb-6 flex-col md:flex-row">
          <div className="flex items-center gap-3"><CheckCircle2 className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Project Workboard</h3><p className="text-sm text-zinc-500">Grouped by workstream with quick filters, collapsed sections, and less vertical waste.</p></div></div>
          {!readOnly ? <Button className="rounded-none" onClick={() => { setEditingTask(null); setShowTaskForm(true); }} type="button"><Plus className="mr-1" size={14} /> Add Task</Button> : null}
        </div>
        {tasks.length === 0 ? <div className="border border-dashed border-zinc-300 p-8 text-center"><p className="text-sm text-zinc-500 mb-3">No tasks yet under this project.</p>{!readOnly ? <Button className="rounded-none" onClick={() => { setEditingTask(null); setShowTaskForm(true); }}><Plus className="mr-1" size={14} /> Create First Task</Button> : null}</div> : null}
        {tasks.length > 0 && visibleTaskCount === 0 ? <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">No tasks match the current search and status filter.</div> : null}
        {visibleTaskCount > 0 ? (
          <div className="space-y-6">
            {taskGroups.map((group) => {
              const isCollapsed = collapsedGroups[group.key] ?? false;
              return (
                <section className="task-group-section" id={`task-group-${group.key}`} key={group.key}>
                  <button className="task-group-header" onClick={() => setCollapsedGroups((current) => ({ ...current, [group.key]: !isCollapsed }))} type="button">
                    <div>
                      <h4 className="text-base font-semibold text-zinc-900">{group.label}</h4>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">{group.tasks.length} tasks · {group.completed} done · {group.blocked} blocked · {group.totalHours} hrs</p>
                    </div>
                    <ChevronDown className={`text-zinc-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} size={18} />
                  </button>
                  {!isCollapsed ? (
                    <div className="mt-4 space-y-4">
                      {group.tasks.map((task) => <TaskCard allTasks={tasks} highlighted={task.id === selectedTaskId} key={task.id} onCreateComment={onCreateComment} onCreateSubtask={onCreateSubtask} onDeleteAttachment={onDeleteAttachment} onDeleteComment={onDeleteComment} onDeleteSubtask={onDeleteSubtask} onDeleteTask={onDeleteTask} onEditTask={(selectedTask) => { setEditingTask(selectedTask); setShowTaskForm(true); }} onUpdateComment={onUpdateComment} onUpdateSubtask={onUpdateSubtask} onUpdateTask={onUpdateTask} onUploadAttachment={onUploadAttachment} projectId={project.id} readOnly={readOnly} task={task} teamMembers={teamMembers} />)}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );

  const ganttPanel = (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center justify-between gap-4 mb-4 flex-col md:flex-row">
          <div className="flex items-center gap-3"><Calendar className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Interactive Gantt</h3><p className="text-sm text-zinc-500">Use this tab for in-page planning, or open the standalone planner window for uninterrupted scheduling.</p></div></div>
          <Button className="rounded-none border-zinc-300" onClick={handleOpenStandaloneGantt} size="sm" type="button" variant="outline">Open Gantt Window</Button>
        </div>
        <GanttChart onOpenTask={(taskId) => { setActiveTab("tasks"); window.setTimeout(() => document.getElementById(`task-${taskId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }} projectCreatedAt={project.created_at} tasks={tasks} />
      </div>
    </div>
  );

  const filesPanel = (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-6"><Paperclip className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Project Files</h3><p className="text-sm text-zinc-500">Aggregated attachments across all project tasks.</p></div></div>
      <div className="space-y-3">
        {projectAttachments.length === 0 ? <p className="text-sm text-zinc-400">No attachments uploaded yet.</p> : projectAttachments.map((attachment) => (
          <div className="border border-zinc-200 bg-white p-4" key={attachment.id}>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-zinc-900">{getAttachmentLabel(attachment)}</p>
                <p className="mt-1 text-sm text-zinc-500">Task: {attachment.task_title}</p>
                <p className="mt-1 text-xs text-zinc-400">{formatFileSize(attachment.size_bytes)} · {formatDateLabel(attachment.uploaded_at)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a className="inline-flex items-center justify-center px-3 py-2 border border-zinc-300 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50" href={attachment.preview_url || attachment.download_url} rel="noreferrer" target="_blank">Preview</a>
                <a className="inline-flex items-center justify-center px-3 py-2 border border-zinc-300 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50" href={attachment.download_url} rel="noreferrer" target="_blank">Open</a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const activityPanel = (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-6"><Activity className="text-zinc-400" size={18} /><div><h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Activity History</h3><p className="text-sm text-zinc-500">Recent changes, assignments, comments, and project events.</p></div></div>
      <div className="space-y-3">
        {activity.length === 0 ? <p className="text-sm text-zinc-400">No recorded activity yet.</p> : activity.map((item) => <div className="border border-zinc-200 p-3 bg-zinc-50/50" key={item.id}><p className="text-sm font-semibold text-zinc-900">{item.message}</p><p className="text-xs text-zinc-400 mt-1">{item.actor_name || "System"} · {formatDateLabel(item.created_at)}</p></div>)}
      </div>
    </div>
  );

  const tabs = [
    ["overview", "Overview"],
    ["tasks", `Tasks (${taskSummary.total})`],
    ["gantt", "Gantt"],
    ["files", `Files (${projectAttachments.length})`],
    ["activity", "Activity"],
  ];

  return (
    <div data-testid="project-detail-view">
      <button className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4 transition-colors" onClick={onBack} type="button">
        <ArrowLeft size={14} />
        <span>{company.name} / {business.name}</span>
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-col xl:flex-row">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FolderKanban className="text-zinc-400" size={24} />
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">{project.name}</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge className={`badge ${getStatusClass(project.status)}`}>{project.status}</Badge>
            <Badge className={`badge ${getPriorityClass(project.priority)}`}>{project.priority} Priority</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a className="btn-secondary" href={exportTasksUrl}>Tasks CSV</a>
          <a className="btn-secondary" href={exportProjectsUrl}>Projects CSV</a>
          <Button className="rounded-none border-zinc-300" onClick={handleGenerateSummary} size="sm" type="button" variant="outline"><Sparkles className="mr-1" size={14} /> {summaryLoading ? "Working..." : "AI Summary"}</Button>
          <Button className="rounded-none border-zinc-300" onClick={handleCreateShare} size="sm" type="button" variant="outline"><Share2 className="mr-1" size={14} /> Share</Button>
          <Button className="rounded-none border-zinc-300" onClick={handleOpenStandaloneGantt} size="sm" type="button" variant="outline"><Calendar className="mr-1" size={14} /> Open Gantt Window</Button>
          {!readOnly ? <Button className="rounded-none border-zinc-300" onClick={onEdit} size="sm" type="button" variant="outline"><Edit className="mr-1" size={14} /> Edit</Button> : null}
          {!readOnly ? <Button className="rounded-none border-red-300 text-red-600 hover:bg-red-50" onClick={onDelete} size="sm" type="button" variant="outline"><Trash2 className="mr-1" size={14} /> Delete</Button> : null}
        </div>
      </div>

      <div className="sticky top-0 z-20 mb-6 border border-zinc-200 bg-white/95 p-3 backdrop-blur">
        <div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">Project Workspace</div>
        <div className="flex flex-wrap gap-2">
          {tabs.map(([key, label]) => (
            <Button className="rounded-none" key={key} onClick={() => setActiveTab(key)} type="button" variant={activeTab === key ? "default" : "outline"}>
              {label}
            </Button>
          ))}
        </div>
      </div>

      {activeTab === "overview" ? overviewPanel : null}
      {activeTab === "tasks" ? tasksPanel : null}
      {activeTab === "gantt" ? ganttPanel : null}
      {activeTab === "files" ? filesPanel : null}
      {activeTab === "activity" ? activityPanel : null}

      <TaskFormDialog availableTasks={tasks.filter((task) => task.id !== editingTask?.id)} initialData={editingTask} loading={submittingTask} onClose={(open) => { if (!open) { setShowTaskForm(false); setEditingTask(null); } }} onSubmit={handleTaskSubmit} open={showTaskForm} teamMembers={teamMembers} />
    </div>
  );
};

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function getTaskGroupKey(task) {
  const tagMatch = (task.tags || []).find((tag) => /^ws\d+/i.test(String(tag).trim()));
  if (tagMatch) return String(tagMatch).trim().toLowerCase();
  const titleMatch = String(task.title || "").match(/^(WS\d+)/i);
  if (titleMatch) return titleMatch[1].toLowerCase();
  return "ungrouped";
}

function formatTaskGroupLabel(groupKey) {
  if (groupKey === "ungrouped") return "General Tasks";
  return groupKey.toUpperCase().replace(/^(WS\d+)$/, "$1 Workstream");
}

function buildGanttTimeline(tasks, projectCreatedAt, zoom = "month") {
  const projectAnchor = normalizeGanttDate(projectCreatedAt);
  const datedTasks = tasks.filter((task) => task.due_date);
  if (datedTasks.length === 0) return null;

  const taskMap = new Map(datedTasks.map((task) => [task.id, task]));
  const scheduledMap = new Map();
  const visiting = new Set();

  const scheduleTask = (task) => {
    if (scheduledMap.has(task.id)) return scheduledMap.get(task.id);
    if (visiting.has(task.id)) {
      const cycleStart = normalizeGanttDate(task.start_date || task.created_at || projectAnchor);
      const cycleEnd = normalizeGanttDate(task.due_date);
      const cycleDurationDays = Math.max(1, Math.round((cycleEnd - cycleStart) / DAY_MS) + 1);
      const cycleScheduled = finalizeScheduledTask(task, cycleStart, cycleEnd >= cycleStart ? cycleEnd : cycleStart, cycleDurationDays);
      scheduledMap.set(task.id, cycleScheduled);
      return cycleScheduled;
    }

    visiting.add(task.id);

    const due = normalizeGanttDate(task.due_date);
    const durationDays = deriveTaskDurationDays(task, projectAnchor, due);
    const explicitStart = task.start_date ? normalizeGanttDate(task.start_date) : null;
    const baseStart = explicitStart || addDays(due, -(durationDays - 1));

    let dependencyStartFloor = baseStart;
    for (const dependencyId of task.dependency_ids || []) {
      const dependencyTask = taskMap.get(dependencyId);
      if (!dependencyTask) continue;
      const dependencyScheduled = scheduleTask(dependencyTask);
      const nextStart = addDays(dependencyScheduled.end, 1);
      if (nextStart > dependencyStartFloor) dependencyStartFloor = nextStart;
    }

    const start = dependencyStartFloor > baseStart ? dependencyStartFloor : baseStart;
    const end = addDays(start, durationDays - 1) > due ? addDays(start, durationDays - 1) : due;
    const scheduled = finalizeScheduledTask(task, start, end, durationDays, due);
    scheduledMap.set(task.id, scheduled);
    visiting.delete(task.id);
    return scheduled;
  };

  const scheduledTasks = datedTasks.map(scheduleTask);
  const minStart = scheduledTasks.reduce((min, task) => (task.start < min ? task.start : min), scheduledTasks[0].start);
  const maxEnd = scheduledTasks.reduce((max, task) => (task.end > max ? task.end : max), scheduledTasks[0].end);
  const rangeStart = floorTimelineDate(addDays(minStart, -1), zoom);
  const rangeEnd = advanceTimelineDate(floorTimelineDate(addDays(maxEnd, 1), zoom), zoom);
  const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / DAY_MS));
  const columns = [];
  let cursor = new Date(rangeStart);
  while (cursor < rangeEnd) {
    const next = advanceTimelineDate(cursor, zoom);
    columns.push({ label: formatTimelineLabel(cursor, zoom), start: new Date(cursor), end: new Date(next) });
    cursor = next;
  }

  const columnMinWidth = zoom === "week" ? 88 : zoom === "month" ? 116 : 160;
  const trackMinWidth = Math.max(columns.length * columnMinWidth, 720);
  const bars = scheduledTasks.map((task) => {
    const offsetDays = Math.max(0, Math.round((task.start - rangeStart) / DAY_MS));
    const durationDays = Math.max(1, Math.round((task.end - task.start) / DAY_MS) + 1);
    const rawLeft = (offsetDays / totalDays) * 100;
    const rawWidth = (durationDays / totalDays) * 100;
    const width = task.isMilestone ? Math.max(rawWidth, 2.4) : Math.max(rawWidth, 3.2);
    const left = task.isMilestone ? Math.max(0, Math.min(100 - width, rawLeft - width / 2)) : rawLeft;
    return { ...task, left, width };
  });

  const today = normalizeGanttDate(new Date().toISOString());
  const todayOffset = today >= rangeStart && today <= rangeEnd ? ((today - rangeStart) / (rangeEnd - rangeStart)) * 100 : null;
  const dependencyCount = bars.reduce((sum, task) => sum + ((task.dependency_ids || []).length), 0);
  const milestoneCount = bars.filter((task) => task.isMilestone).length;

  return { columns, bars, todayOffset, dependencyCount, milestoneCount, columnMinWidth, trackMinWidth };
}

function finalizeScheduledTask(task, start, end, durationDays, plannedDue = null) {
  const safeEnd = end >= start ? end : start;
  const isMilestone = isGanttMilestone(task, start, safeEnd);
  const phaseTag = (task.tags || []).find((tag) => String(tag).toLowerCase().includes("phase")) || "";
  const lateByDays = plannedDue && safeEnd > plannedDue ? Math.round((safeEnd - plannedDue) / DAY_MS) : 0;
  return { ...task, start, end: safeEnd, durationDays, isMilestone, phaseTag, lateByDays, plannedDue: plannedDue || safeEnd };
}

function deriveTaskDurationDays(task, projectAnchor, due) {
  const explicitStart = task.start_date ? normalizeGanttDate(task.start_date) : null;
  if (explicitStart && due >= explicitStart) return Math.max(1, Math.round((due - explicitStart) / DAY_MS) + 1);
  const estimatedHours = Number(task.estimated_hours || 0);
  if (estimatedHours > 0) return Math.max(1, Math.ceil(estimatedHours / 8));
  const createdStart = normalizeGanttDate(task.created_at || projectAnchor);
  if (due >= createdStart) return Math.max(1, Math.round((due - createdStart) / DAY_MS) + 1);
  return 1;
}

function normalizeGanttDate(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return new Date();
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function addDays(date, amount) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function floorTimelineDate(date, zoom) {
  if (zoom === "week") {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    return addDays(date, diff);
  }
  if (zoom === "quarter") {
    const quarterMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterMonth, 1);
  }
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function advanceTimelineDate(date, zoom) {
  if (zoom === "week") return addDays(date, 7);
  if (zoom === "quarter") return new Date(date.getFullYear(), date.getMonth() + 3, 1);
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

function formatTimelineLabel(date, zoom) {
  if (zoom === "week") {
    const end = addDays(date, 6);
    return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }
  if (zoom === "quarter") {
    return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
  }
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function isGanttMilestone(task, start, end) {
  const tags = (task.tags || []).map((tag) => String(tag).toLowerCase());
  const title = String(task.title || "").toLowerCase();
  return tags.includes("milestone") || tags.includes("phase-gate") || title.startsWith("phase ") || title.startsWith("milestone ") || Math.round((end - start) / DAY_MS) === 0;
}

const DetailCard = ({ icon, label, value, isMono }) => <div className="card p-5"><div className="flex items-center gap-2 text-zinc-400 mb-2">{icon}<span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{label}</span></div><p className={`text-lg font-medium text-zinc-900 ${isMono ? "font-mono" : ""}`}>{value}</p></div>;
const TaskSummaryCard = ({ label, value }) => <div className="border border-zinc-200 bg-zinc-50 p-4"><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p><p className="mt-2 text-2xl font-semibold text-zinc-900">{value}</p></div>;
const ListBlock = ({ title, items = [] }) => <div><p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">{title}</p>{items?.length ? <ul className="space-y-2 text-sm text-zinc-700">{items.map((item, index) => <li className="border-l-2 border-zinc-300 pl-3" key={`${title}-${index}`}>{item}</li>)}</ul> : <p className="text-sm text-zinc-400">None.</p>}</div>;
